import { ReactNode, useContext } from 'react';
import { Button } from '@shared/components/Button';
import { Icon, IconName } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { ColorThemeContext, grid } from '@shared/styles';
import { GRID_UNIT } from '@shared/styles/constants';

export interface MBEmptyListPlaceholderProps {
  title?: string;
  description?: string;
  /** Rendered as is: the caller sizes and tints it. */
  image?: ReactNode;
  /** A line-art icon instead of an `image`, tinted with `imageTintColor` and sized by `imageSize`. */
  icon?: IconName;
  imageTintColor?: string;
  imageSize?: number;
  imagePosition?: 'above' | 'below';
  imageVerticalGap?: number;
  buttonTitle?: string;
  onButtonPress?(): void;
  testID?: string;
  className?: string;
}

export function MBEmptyListPlaceholder({
  title,
  description,
  image,
  icon,
  imageTintColor,
  imageSize = grid(24),
  imagePosition = 'above',
  imageVerticalGap = 2,
  buttonTitle,
  onButtonPress,
  testID,
  className,
}: MBEmptyListPlaceholderProps) {
  const colors = useContext(ColorThemeContext);

  const tintColor = imageTintColor !== undefined ? imageTintColor : colors.highlightActive;
  const imageElement = getImageElement({ image, icon, tintColor, imageSize });
  const hasImage = !!imageElement;

  return (
    <View fill center padding={2} testID={testID} className={className}>
      {imagePosition === 'above' && imageElement}
      {imagePosition === 'above' && hasImage && <View paddingTop={imageVerticalGap} />}
      {!!title && (
        <Text size="h4" align="center" balance style={styles.title}>
          {title}
        </Text>
      )}
      {/* Half the other gaps: the title's tall line height already adds space below it. */}
      <View paddingTop={1} />
      {!!description && (
        <Text size="body1" inactive align="center" balance style={styles.description}>
          {description}
        </Text>
      )}
      {imagePosition === 'below' && hasImage && <View paddingTop={imageVerticalGap} />}
      {imagePosition === 'below' && imageElement}
      <View paddingTop={2} />
      {!!buttonTitle && !!onButtonPress && (
        <Button variant="secondary" size="medium" label={buttonTitle} onClick={onButtonPress} />
      )}
    </View>
  );
}

function getImageElement({
  image,
  icon,
  tintColor,
  imageSize,
}: {
  image: ReactNode;
  icon: IconName | undefined;
  tintColor: string;
  imageSize: number;
}) {
  if (icon) {
    return <Icon name={icon} color={tintColor} gridSize={imageSize / GRID_UNIT} />;
  }
  return image ?? undefined;
}

const styles = {
  title: {
    maxWidth: grid(60),
  },
  description: {
    maxWidth: grid(60),
  },
};
